export enum GTMEvent {
  COVIDEO_EVENT = 'covideo-event',
  COVIDEO_PAGEVIEW = 'covideo-pageview',
}

export enum GTMEventName {
  CTA_INTERACTION = 'CTA Interaction',
}

export enum GTMPageApplication {
  CRM_VIDEO_ADMIN = 'CRM Video Admin',
}

export enum PageLocation {
  LANDING_PAGE = 'Landing Page',
}

export enum PageSection {
  OVERVIEW = 'Overview',
  PREVIEW = 'Preview',
}

export interface GTMCDKUserAttributes {
  /**
   * Unique identifier of the user within our system.
   * @example "2343564"
   */
  user_id?: string;

  /**
   * One or more roles assigned to the user.
   * @example ["Sales Person", "F&I Manager"]
   */
  user_role?: string[];

  /**
   * Enterprise identifier (CDK enterprise code).
   * @example "E000118"
   */
  enterprise_id?: string;

  /**
   * Enterprise (dealer group) display name.
   * @example "Excellence Motors"
   */
  enterprise_name?: string;

  /**
   * Store identifier (CDK store code).
   * @example "S13552"
   */
  store_id?: string;

  /**
   * Store display name.
   * @example "Toyota Boston"
   */
  store_name?: string;

  /**
   * Customer identifier from the CRM (if present).
   * @example "123454"
   */
  customer_id?: string;

  /**
   * Flag indicating whether the user is an internal user (CDK/Covideo staff).
   * @example true
   */
  is_internal_user?: boolean;
}

// Pageview Attributes
export interface GTMPageviewAttributes {
  /**
   * Event name for GTM or analytics tracking.
   * Always set to `'covideo-pageview'`.
   * @example "covideo-pageview"
   */
  event: GTMEvent.COVIDEO_PAGEVIEW;

  /**
   * Application or product identifier where the pageview originated.
   * Typically fixed to `'CRM Video Embed'`.
   * @example "'CRM Video Embed'"
   */
  application: GTMPageApplication.CRM_VIDEO_ADMIN;

  /**
   * Full URL of the page viewed, including protocol, domain, and path.
   * @example "https://video.connectcdk.com/reports/companies"
   */
  page_url: string;

  /**
   * Higher-level section or context of the page being viewed
   * (e.g., "Reports", "Library", "Dashboard").
   * @example "Reports"
   */
  page_location?: string;
  /**
   * **Lower-level location within the selected `page_section`.**
   * Represents a specific page or sub-route under that section.
   * @example "My Team"
   */
  page_section?: string;
}

// Event Attributes
export interface GTMEventAttributes {
  /**
   * Event name for GTM or analytics tracking.
   * Always set to `'covideo-name'`.
   * @example "covideo-name"
   */
  event: GTMEvent.COVIDEO_EVENT;

  /**
   * Specific name of the event being tracked.
   * @example "CTA Interaction"
   */
  event_name: GTMEventName;
}

// CDK User Attributes
export interface GTMCDKUserAttributes {
  /**
   * Unique identifier of the user within our system.
   * @example "2343564"
   */
  user_id?: string;

  /**
   * One or more roles assigned to the user.
   * @example ["Sales Person", "F&I Manager"]
   */
  user_role?: string[];

  /**
   * Enterprise identifier (CDK enterprise code).
   * @example "E000118"
   */
  enterprise_id?: string;

  /**
   * Enterprise (dealer group) display name.
   * @example "Excellence Motors"
   */
  enterprise_name?: string;

  /**
   * Store identifier (CDK store code).
   * @example "S13552"
   */
  store_id?: string;

  /**
   * Store display name.
   * @example "Toyota Boston"
   */
  store_name?: string;

  /**
   * Customer identifier from the CRM (if present).
   * @example "123454"
   */
  customer_id?: string;

  /**
   * Flag indicating whether the user is an internal user (CDK/Covideo staff).
   * @example true
   */
  is_internal_user?: boolean;
}

// Video Attributes
export interface GTMVideoAttributes {
  /**
   * The ID of the video that is selected, created, or currently present on the page.
   * @example 987654
   */
  video_id?: number;

  /**
   * The category/type associated with the video.
   * Common examples: "Marketing", "Prospecting", "Respond to Inquiry", "Thanks for Your Business".
   * @example "Marketing"
   */
  video_type?: string | undefined;

  /**
   * Whether the video has the customer's name filled in.
   * NOTE: By cdk documentation this is a boolean check if that is correct.
   * @example true
   */
  video_customer_name?: boolean;

  /**
   * Length of the video in **minutes** as a decimal value.
   * For example, "00:02:45" (2 minutes 45 seconds) -> 2.75 minutes.
   * @example 2.75
   */
  video_length?: number;

  /**
   * Title of the video.
   * NOTE: By cdk documentation this is The inventory ID of the vehicle related to this video (as provided for tracking).
   * @example 123456
   */
  video_title?: string;
}

// Vehicle Attributes
export interface GTMVehicleAttributes {
  /**
   * Vehicle description.
   * @example "2019 Ford Taurus"
   */
  vehicle?: string;

  /**
   * Vehicle price in numeric form (no currency symbol).
   * @example 17499
   */
  price?: number;

  /**
   * Vehicle Identification Number (VIN).
   * @example "1FAHP2E89KG117472"
   */
  vin?: string;

  /**
   * Dealer stock number.
   * @example "AU117472"
   */
  stock_number?: string;

  /**
   * Vehicle condition (e.g., New, Used, CPO).
   * @example "Used"
   */
  condition?: string;

  /**
   * Engine details or code.
   * @example "3.5L Ti-VCT V6 (FFV)"
   */
  engine?: string;

  /**
   * Manufacturer / make.
   * @example "Ford"
   */
  make?: string;

  /**
   * Vehicle model.
   * @example "Taurus"
   */
  model?: string;

  /**
   * Trim and/or body style.
   * @example "SEL 4dr Front-wheel Drive Sedan"
   */
  trim?: string;

  /**
   * Model year.
   * @example 2019
   */
  year?: number;

  /**
   * Odometer reading in miles.
   * @example 39231
   */
  mileage?: number;

  /**
   * Exterior color.
   * @example "Red"
   */
  ext_color?: string;

  /**
   * Interior color.
   * @example "Dune"
   */
  int_color?: string;

  /**
   * Transmission type.
   * @example "Automatic"
   */
  transmission?: string;
}

export interface GTMVideoMessageProperties {
  customer_id?: string;
  sms?: boolean;
  email?: boolean;
  /**
   * Indicates whether the user opted to notify the recipient after sharing
   * the video message (e.g., "Notify viewer when video is viewed").
   *
   * @example true // user enabled notifications for video views
   */
  notification_viewed?: boolean;

  /**
   * The name or ID of the selected landing page template used for the
   * shared video. Typically derived from `templateId` or the template label.
   *
   * @example "Automotive Default Template"
   */
  landing_page?: string;

  /**
   * The name or ID of the selected Call-to-Action (CTA) link set
   * attached to the shared video. Usually maps to a `linksetId` or title.
   *
   * @example "Book a Test Drive"
   */
  call_to_action?: string;

  /**
   * The name or ID of the selected video thumbnail style or icon used
   * in the share configuration. Usually maps to `iconId` or template title.
   *
   * @example "Dark Overlay Thumbnail"
   */
  thumbnails?: string;
}

// covideo mapper video and vehicle attributes
export interface CovideoGTMVideoAttributes {
  videoId?: number;
  videoType?: string;
  videoCustomerName?: boolean;
  videoLength?: number;
  videoTitle?: string;
}

export interface CovideoGTMVehicleItem {
  title: string;
  price: string | number;
  year: string | number;
  mileage: string | number;
  vin: string;
  stockNumber: string | number;
  itemCondition: string;
  engine: string;
  make: string;
  model: string;
  trim: string;
  exteriorColor: string;
  interiorColor: string;
  transmission: string;
}

export interface CovideoGTMVideoMessageProperties {
  customerId?: string;
  sms?: boolean;
  email?: boolean;

  notificationViewed?: boolean;

  landingPage?: string;

  callToAction?: string;

  thumbnails?: string;
}
